import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import {
  Sparkles, Loader2, Copy, Check, RefreshCw, FileText, BookOpen,
  ExternalLink, AlertCircle, Wand2,
} from "lucide-react";

/**
 * Visionary Long-form Ad Copy admin tab.
 *
 * Generates 350-450 word curiosity-gap Facebook ad copy via Claude
 * Sonnet 4.5 (Emergent LLM key). Output is plain-text copy ready to
 * paste into Facebook Ads Manager — the ad will link to the existing
 * /snapshot tripwire landing page, so no new funnel is needed (hybrid
 * architecture choice C from the planning step).
 *
 * Flow:
 *  1. Admin picks hook angle (curiosity gap framing) + persona
 *  2. Click "Vygenerovať" → POST /api/admin/visionary-copy/generate
 *  3. Output card shows headline + body + CTA label
 *  4. One-click "Copy" buttons for each field
 *  5. Recent generations list on the right for re-use
 */

function adminHeaders() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return u.email ? { "X-User-Email": u.email } : {};
  } catch {
    return {};
  }
}

export default function AdminVisionaryCopyTab() {
  const [meta, setMeta] = useState(null);
  const [hookAngle, setHookAngle] = useState("");
  const [persona, setPersona] = useState("");
  const [productFocus, setProductFocus] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState("");
  const [copiedKey, setCopiedKey] = useState("");

  // Initial load: meta + recent
  useEffect(() => {
    (async () => {
      try {
        const [m, l] = await Promise.all([
          axios.get(`${API}/admin/visionary-copy/meta`, { headers: adminHeaders() }),
          axios.get(`${API}/admin/visionary-copy/list?limit=20`, { headers: adminHeaders() }),
        ]);
        setMeta(m.data);
        setRecent(l.data?.items || []);
        if (!hookAngle && m.data?.hook_angles?.length) {
          setHookAngle(m.data.hook_angles[0].key);
        }
        if (!persona && m.data?.personas?.length) {
          setPersona(m.data.personas[0].key);
        }
        if (!targetUrl && m.data?.default_target_url) {
          setTargetUrl(m.data.default_target_url);
        }
      } catch (e) {
        console.error("visionary meta load failed", e);
        setError(e?.response?.data?.detail || "Načítanie metadát zlyhalo.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshRecent = useCallback(async () => {
    try {
      const l = await axios.get(`${API}/admin/visionary-copy/list?limit=20`, {
        headers: adminHeaders(),
      });
      setRecent(l.data?.items || []);
    } catch (e) {
      console.error("recent reload failed", e);
    }
  }, []);

  const generate = useCallback(async () => {
    if (!hookAngle || !persona) return;
    setGenerating(true);
    setError("");
    try {
      const r = await axios.post(
        `${API}/admin/visionary-copy/generate`,
        {
          hook_angle: hookAngle,
          persona,
          product_focus: productFocus || undefined,
          target_url: targetUrl || undefined,
        },
        { headers: adminHeaders() },
      );
      setResult(r.data);
      // Best-effort: refresh recent in background.
      refreshRecent();
      // Update remaining quota inline.
      if (typeof r.data?.quota_remaining === "number") {
        setMeta((prev) => prev ? { ...prev, quota_remaining: r.data.quota_remaining } : prev);
      }
    } catch (e) {
      console.error("visionary generate failed", e);
      setError(e?.response?.data?.detail || "Generovanie zlyhalo.");
    }
    setGenerating(false);
  }, [hookAngle, persona, productFocus, targetUrl, refreshRecent]);

  const copyField = useCallback((key, value) => {
    if (!value) return;
    try {
      navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 1400);
    } catch {
      /* clipboard blocked – noop */
    }
  }, []);

  const openHistorical = useCallback((item) => {
    setResult(item);
    // Pre-fill form so re-generate uses the same shape if admin tweaks.
    setHookAngle(item.hook_angle || hookAngle);
    setPersona(item.persona || persona);
    setProductFocus(item.product_focus || "");
    setTargetUrl(item.target_url || targetUrl);
  }, [hookAngle, persona, targetUrl]);

  return (
    <div data-testid="admin-visionary-copy-tab" className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-purple-600" />
            Visionary Longform Copy
          </h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Dlhý 350–450 slovný Facebook ad text v slovenčine, generovaný
            cez Claude Sonnet 4.5. Curiosity-gap štýl (Alex Mehr).
            Linkuje na existujúci €9 <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">/snapshot</code> tripwire.
          </p>
        </div>
        {meta && (
          <div
            data-testid="visionary-quota-badge"
            className="text-xs font-semibold px-3 py-1.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200"
          >
            Denná kvóta: {meta.quota_remaining}/50
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form column */}
        <div className="lg:col-span-1 space-y-4 bg-white border border-gray-200 rounded-xl p-5">
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
              Hook angle
            </label>
            <select
              data-testid="visionary-hook-select"
              value={hookAngle}
              onChange={(e) => setHookAngle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900"
            >
              {meta?.hook_angles?.map((h) => (
                <option key={h.key} value={h.key}>{h.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
              Cieľová persona
            </label>
            <select
              data-testid="visionary-persona-select"
              value={persona}
              onChange={(e) => setPersona(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900"
            >
              {meta?.personas?.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
              Product focus (voliteľné)
            </label>
            <textarea
              data-testid="visionary-product-focus"
              value={productFocus}
              onChange={(e) => setProductFocus(e.target.value)}
              placeholder="Nechaj prázdne pre default opis TopRankPulse…"
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900 font-mono"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
              CTA link
            </label>
            <input
              type="url"
              data-testid="visionary-target-url"
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900 font-mono"
            />
          </div>

          <button
            data-testid="visionary-generate-btn"
            type="button"
            onClick={generate}
            disabled={generating || !hookAngle || !persona}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-900 text-white rounded-lg text-sm font-bold hover:bg-black disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generujem (Claude Sonnet 4.5)…
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" />
                Vygenerovať copy
              </>
            )}
          </button>

          {error && (
            <div
              data-testid="visionary-error"
              className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Result column */}
        <div className="lg:col-span-2 space-y-4">
          {!result ? (
            <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-10 text-center text-gray-400">
              <Sparkles className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">
                Vygenerovaný copy sa zobrazí tu. Vyber hook angle + personu a klikni vygenerovať.
              </p>
            </div>
          ) : (
            <div data-testid="visionary-result" className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-xs">
                  <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded font-semibold">
                    {result.hook_angle_label || result.hook_angle}
                  </span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded font-semibold">
                    {result.persona}
                  </span>
                  <span
                    data-testid="visionary-word-count"
                    className={`px-2 py-1 rounded font-mono ${
                      result.word_count >= 350 && result.word_count <= 450
                        ? "bg-green-50 text-green-700"
                        : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {result.word_count} slov
                  </span>
                </div>
              </div>

              <Field
                label="Headline (primary text top)"
                value={result.headline}
                fieldKey="headline"
                copiedKey={copiedKey}
                onCopy={copyField}
                testid="visionary-headline"
              />

              <BodyField
                label={`Body (${result.word_count} slov)`}
                value={result.body}
                copiedKey={copiedKey}
                onCopy={copyField}
              />

              <Field
                label="CTA button label"
                value={result.cta_label}
                fieldKey="cta_label"
                copiedKey={copiedKey}
                onCopy={copyField}
                testid="visionary-cta"
              />

              <div className="pt-2 border-t border-gray-100 flex items-center gap-3 text-xs text-gray-500">
                <ExternalLink className="w-3.5 h-3.5" />
                <span>Linkuje na:</span>
                <a
                  href={result.target_url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono text-purple-700 hover:underline"
                >
                  {result.target_url}
                </a>
              </div>
            </div>
          )}

          {/* Recent generations */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Posledné vygenerované ({recent.length})
              </h3>
              <button
                type="button"
                onClick={refreshRecent}
                data-testid="visionary-refresh-list"
                className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Obnoviť
              </button>
            </div>

            {recent.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">
                Zatiaľ nič vygenerované.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {recent.map((it) => (
                  <li key={it.id}>
                    <button
                      type="button"
                      data-testid={`visionary-recent-${it.id}`}
                      onClick={() => openHistorical(it)}
                      className="w-full text-left py-2.5 px-2 hover:bg-gray-50 rounded transition-colors"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-semibold text-gray-900 truncate">
                          {it.headline || "(bez headline)"}
                        </span>
                        <span className="text-[10px] font-mono text-gray-400 flex-shrink-0">
                          {(it.created_at || "").slice(0, 16).replace("T", " ")}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-500">
                        <span>{it.hook_angle_label || it.hook_angle}</span>
                        <span>·</span>
                        <span>{it.persona}</span>
                        <span>·</span>
                        <span>{it.word_count} slov</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, fieldKey, copiedKey, onCopy, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
          {label}
        </label>
        <button
          type="button"
          onClick={() => onCopy(fieldKey, value)}
          data-testid={`${testid}-copy-btn`}
          className="flex items-center gap-1 text-[11px] font-semibold text-gray-600 hover:text-gray-900"
        >
          {copiedKey === fieldKey ? (
            <>
              <Check className="w-3.5 h-3.5 text-green-600" />
              <span className="text-green-600">Skopírované</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              <span>Kopírovať</span>
            </>
          )}
        </button>
      </div>
      <div
        data-testid={testid}
        className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 font-medium break-words"
      >
        {value || <em className="text-gray-400">prázdne</em>}
      </div>
    </div>
  );
}

function BodyField({ label, value, copiedKey, onCopy }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
          {label}
        </label>
        <button
          type="button"
          onClick={() => onCopy("body", value)}
          data-testid="visionary-body-copy-btn"
          className="flex items-center gap-1 text-[11px] font-semibold text-gray-600 hover:text-gray-900"
        >
          {copiedKey === "body" ? (
            <>
              <Check className="w-3.5 h-3.5 text-green-600" />
              <span className="text-green-600">Skopírované</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              <span>Kopírovať celé telo</span>
            </>
          )}
        </button>
      </div>
      <pre
        data-testid="visionary-body"
        className="px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 whitespace-pre-wrap font-sans leading-relaxed max-h-[420px] overflow-auto"
      >
        {value || <em className="text-gray-400">prázdne</em>}
      </pre>
    </div>
  );
}
